import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { Table, TableCellDropdown, TableCellImgLabel, Spinner, Toggle, EmptyState } from '../../components/widgets';
import QuestionMark from '../../components/widgets/QuestionMark';
import css from './DataConnectorAccounts.css';
import { useFetchAccountSourceMapping } from '../hooks';
import { getAccountSourceMappingList, parseAccountsData, hasAccountsTimezone } from '../utils';

const thStyle = { padding: '8px', textAlign: 'center', fontWeight: 600 };
const tdStyle = { height: '56px' };

// Distinguishes "the network has not reported a timezone for this account" from a real value, so an empty
// cell is never mistaken for a missing feature
const TableCellTimezoneNotSet = ({ tdStyle: style }) => (
    <td style={style}>
        <div className={css.notSetCell}>
            <Translate id="STATIC.PAGES.DATA_SOURCES.SHELF.ACCOUNTS.TIMEZONE_NOT_SET" />
            <QuestionMark
                containerClassName={css.notSetQuestionMark}
                message="STATIC.PAGES.DATA_SOURCES.SHELF.ACCOUNTS.TIMEZONE_NOT_SET_TOOLTIP"
            />
        </div>
    </td>
);

TableCellTimezoneNotSet.propTypes = {
    tdStyle: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

const DataConnectorAccounts = ({
    isEmptyMccId = false,
    adnId = null,
    accountsData = null,
    onToggleSelectAll = () => {},
    onToggleSingleRow = () => {},
    onAccountSourceMapChange = () => {},
    newAccountsToggle = false,
    onAutoAddAccounts = () => {},
    showViewAs = false,
    adminMode = false,
}) => {
    const accountSourceMapResponse = useFetchAccountSourceMapping(adnId);
    // The timezone columns are an internal facing feature for now, so they are admin only
    const showTimezone = adminMode && hasAccountsTimezone(accountsData);
    // The table is fixed at 640px, so the optional columns have to share it rather than overflow into a
    // horizontal scroll. Both optional columns present is the tightest case (Facebook, Google Ads).
    const getNameColumnWidth = () => {
        if (showViewAs && showTimezone) {
            return '160px';
        }
        return showViewAs || showTimezone ? '215px' : '450px';
    };
    const nameColumnWidth = getNameColumnWidth();
    const accountIdColumnWidth = showViewAs && showTimezone ? '115px' : '140px';

    const tableMetadata = [
        {
            name: 'name',
            cellComponent: TableCellImgLabel,
            displayName: 'Account Name',
            placeholder: 'account name',
            headerAlign: 'center',
            thStyle: { ...thStyle, width: nameColumnWidth },
            cellValues: [
                { propName: 'subText', dataKey: 'subText' },
                { propName: 'ImgComponent', dataKey: 'imgComponent' },
                { propName: 'SideImg', dataKey: 'sideImg' },
                { propName: 'sideImgHoverText', dataKey: 'sideImgHoverText' },
            ],
            cellProps: {
                tdStyle: { ...tdStyle, maxWidth: nameColumnWidth },
                useDefaultEmptyImg: false,
                imgStyle: {
                    height: '18px',
                    maxWidth: '18px',
                },
                subTextStyle: {
                    fontSize: '11px',
                    fontStyle: 'italic',
                    color: '#FFAA20',
                },
                reverseOrder: true,
            },
        },
        {
            name: 'id',
            displayName: 'Account ID',
            placeholder: 'account ID',
            headerAlign: 'center',
            thStyle: { ...thStyle, width: accountIdColumnWidth },
            cellProps: {
                tdStyle,
            },
        },
    ];

    if (showTimezone) {
        tableMetadata.push({
            name: 'timezoneDisplay',
            displayName: 'Timezone',
            placeholder: 'timezone',
            headerAlign: 'center',
            headerTooltip: 'STATIC.PAGES.DATA_SOURCES.SHELF.ACCOUNTS.TIMEZONE_TOOLTIP',
            thStyle: { ...thStyle, width: showViewAs ? '125px' : '180px' },
            getCellComponent: row => (row.hasTimezone ? undefined : TableCellTimezoneNotSet),
            cellProps: {
                tdStyle,
                className: css.timezoneCell,
            },
        });
    }

    if (showViewAs) {
        tableMetadata.push({
            name: 'source',
            displayName: 'View This Source As:',
            placeholder: 'source',
            headerAlign: 'center',
            thStyle: { ...thStyle },
            cellComponent: TableCellDropdown,
            headerTooltip: 'STATIC.PAGES.DATA_SOURCES.SHELF.ACCOUNTS.VIEW_AS_TOOLTIP',
            cellProps: {
                valuesList: getAccountSourceMappingList(accountSourceMapResponse),
                tdStyle: { ...tdStyle, padding: '10px 14px' },
                onValueSelection: (newSourceMap, rowIndex) => onAccountSourceMapChange(newSourceMap, rowIndex),
            },
            cellValues: [
                { dataKey: 'selected', propName: 'selected' },
                { dataKey: 'disabled', propName: 'disabled' },
            ],
        });
    }

    return (
        <div className={css.accountsTableContainer}>
            {isEmptyMccId ? (
                <div className={css.noAccountsContainer}>
                    <EmptyState icon="happyPage" header="STATIC.PAGES.DATA_SOURCES.SHELF.EMPTY_MCC_ID" />
                </div>
            ) : !accountsData ? (
                <React.Fragment>
                    <div className={css.loaderContainer}>
                        <Spinner show style={{ width: '64px' }} />
                        <div className={css.loadingText}>
                            <Translate id="STATIC.PAGES.DATA_SOURCES.SHELF.LOADING_ACCOUNTS" />
                        </div>
                    </div>
                </React.Fragment>
            ) : accountsData.length === 0 ? (
                <div className={css.noAccountsContainer}>
                    <EmptyState icon="default" header="STATIC.PAGES.DATA_SOURCES.SHELF.NO_AD_ACCOUNTS" />
                </div>
            ) : (
                accountsData.length > 0 && (
                    <React.Fragment>
                        <div className={css.toggleRowContainer}>
                            <div className={css.toggleRowContent}>
                                <span className={css.accountsHeader}>Accounts</span>
                                <Toggle
                                    style={{ width: '270px' }}
                                    checked={newAccountsToggle}
                                    label={
                                        <Translate id="STATIC.PAGES.DATA_SOURCES.SHELF.ACCOUNTS.AUTOMATICALLY_ACTIVATE" />
                                    }
                                    onToggle={onAutoAddAccounts}
                                />
                            </div>
                        </div>
                        <div className={css.tableContainer}>
                            <Table
                                metadata={tableMetadata}
                                data={parseAccountsData(accountsData)}
                                zebraTable={false}
                                selectable
                                filterable
                                onToggleSelectAll={onToggleSelectAll}
                                onToggleSingleRow={onToggleSingleRow}
                                thClass={css.tableHeader}
                                tableClass={css.table}
                                thFiltersClass={css.tableFiltersHeader}
                            />
                        </div>
                    </React.Fragment>
                )
            )}
        </div>
    );
};

DataConnectorAccounts.propTypes = {
    isEmptyMccId: PropTypes.bool,
    adnId: PropTypes.number,
    accountsData: PropTypes.arrayOf(PropTypes.any),
    newAccountsToggle: PropTypes.bool,
    showViewAs: PropTypes.bool,
    adminMode: PropTypes.bool,
    onToggleSelectAll: PropTypes.func,
    onToggleSingleRow: PropTypes.func,
    onAccountSourceMapChange: PropTypes.func,
    onAutoAddAccounts: PropTypes.func,
};

export default DataConnectorAccounts;
